'use client';

import { useMemo } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';

import { useGlobalUI } from 'contexts/GlobalUIContext';
import ScienceBreadcrumb, {
  SCIENCE_ROOT_CRUMB,
} from '../studies/ScienceBreadcrumb';
import RichText, { ReferenceList } from '../studies/RichText';
import ASK_QUESTIONS, { ASK_CATEGORY_LABEL, type AskQuestion } from './askData';
import { CATEGORY_DOT } from './askTokens';
import type { AskArticleProps, AskFigureData } from './askArticles';
import AskFigure from './AskFigure';
import styles from './AskDetail.module.css';

type Block = { html: string } | { figureId: string };

/* Split on extractor-emitted markers so this can run on the server without a DOM. */
const toBlocks = (html: string): Block[] => {
  const out: Block[] = [];
  const re = /<div data-figure="([^"]+)"><\/div>/g;
  let last = 0;
  let m = re.exec(html);
  while (m) {
    if (m.index > last) out.push({ html: html.slice(last, m.index) });
    out.push({ figureId: m[1] });
    last = m.index + m[0].length;
    m = re.exec(html);
  }
  if (last < html.length) out.push({ html: html.slice(last) });
  return out;
};

/* The tail of every Ask body is, in order: the standing disclaimer
   (<p class="a-note">) and then a "Go deeper" heading with its links. Both
   are peeled off here so the page can close in the order a reader expects —
   article, sources, disclaimer, where to read next — rather than leaving the
   references stranded behind the disclaimer and the links. An article
   missing either part is unaffected. */
const GO_DEEPER = /<h2>\s*Go deeper\s*<\/h2>/i;

/* The disclaimer, found by EITHER its class or its opening words.
 *
 * The class alone was not enough. Anton's rich-text editor drops `class` from a
 * paragraph whenever the body is edited, so the four articles Science edited on
 * 24 Sep — magnesium, hot flashes, sauna, bedroom air — arrived with a bare
 * `<p>`. The peel below then found nothing, the paragraph stayed inside the
 * body, and the standing disclaimer rendered before the references instead of
 * after them. Nothing errored. Every NEW article written in Anton would have
 * shipped that way from the start, because the editor cannot add the class.
 *
 * The text anchor is safe: all 13 published questions open this paragraph with
 * the byte-identical "This is an observational analysis of Ultrahuman member
 * data", checked against production on 30 Sep. `[^>]*` so it matches with or
 * without the class, and `\s*` because the editor also reflows whitespace. */
const DISCLAIMER =
  /<p class="a-note">|<p[^>]*>\s*This is an observational analysis/i;

const splitTail = (
  html: string,
): { bodyHtml: string; noteHtml: string; goDeeperHtml: string } => {
  const g = GO_DEEPER.exec(html);
  const goDeeperHtml = g ? html.slice(g.index) : '';
  const head = g ? html.slice(0, g.index) : html;

  const d = DISCLAIMER.exec(head);
  return {
    bodyHtml: d ? head.slice(0, d.index) : head,
    noteHtml: d ? head.slice(d.index) : '',
    goDeeperHtml,
  };
};

const AskDetail = ({
  article,
  question,
  figures,
  siblings,
}: {
  article: AskArticleProps;
  question: AskQuestion;
  figures: Record<string, AskFigureData>;
  /* Every question, for resolving the "Go deeper" links to a card. Passed in
     by the route so the titles follow the CMS; defaults to the vendored
     extract, which is what the links used to resolve against unconditionally
     — a question retitled in Anton kept its old title on every card that
     pointed at it. */
  siblings?: AskQuestion[];
}) => {
  /* Header stays put on scroll. Animating a fixed, backdrop-blurred layer
     forces the compositor to re-sample the backdrop on every direction change,
     which reads as the page flickering — the same reason the byte and accuracy
     detail pages pin it. Not redundant with the default: the studies hub sets
     headerCollaspable: true, so a client nav from the rail into a question
     would otherwise arrive with a collapsible header. */
  useGlobalUI({ headerCollaspable: false });

  const t = useTranslations('science');
  const primary = question.categories[0];
  /* Who wrote it. Unlike the review line this carries no date and makes no
     claim about a version, so a name alone is the whole statement and it
     renders on its own. Empty today on all 13 questions — the column exists
     and the admin API writes it, nobody has filled it in. */
  const authors = article.authors?.length
    ? article.authors.map((a) => a.name).join(', ')
    : '';
  /* First reviewer only. */
  const reviewer = article.reviewedBy?.[0];
  /* Both, or neither. A byline asserts that a named person reviewed THIS
     version, so a name with no date asserts it about no version in particular.
     Ask has no `medically_reviewed` boolean to check as well — and is not
     getting one — so for Ask these two ARE the whole claim. */
  const namedReview =
    reviewer && article.lastReviewed ? reviewer : undefined;
  /* Peel the disclaimer and "Go deeper" off the body so sources render first. */
  const { bodyHtml, noteHtml, goDeeperHtml } = useMemo(
    () => splitTail(article.bodyHtml),
    [article.bodyHtml],
  );
  /* "Go deeper" links become hub cards. A slug that no longer matches is dropped. */
  const goDeeper = useMemo(() => {
    const slugs = Array.from(
      goDeeperHtml.matchAll(/href="\/science\/ask\/([a-z0-9-]+)\/?"/g),
    ).map((m) => m[1]);
    const seen = new Set<string>();
    return slugs
      .filter((slug) => (seen.has(slug) ? false : (seen.add(slug), true)))
      .filter((slug) => slug !== question.slug)
      .map((slug) => (siblings ?? ASK_QUESTIONS).find((q) => q.slug === slug))
      .filter((q): q is AskQuestion => Boolean(q));
  }, [goDeeperHtml, question.slug, siblings]);
  const blocks = useMemo(() => toBlocks(bodyHtml), [bodyHtml]);
  const figureMeta = useMemo(
    () => new Map(article.figures.map((f) => [f.id, f])),
    [article.figures],
  );

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <div className={styles.headingBlock}>
          <ScienceBreadcrumb
            dark
            crumbs={[
              SCIENCE_ROOT_CRUMB,
              {
                label: t('science.ask.heading'),
                href: '/science/studies#questions',
              },
              { label: question.shortLabel },
            ]}
          />

          <div className={styles.eyebrowRow}>
            <span className={styles.categoryPill}>
              <span
                className={styles.categorySwatch}
                style={{ background: CATEGORY_DOT[primary] }}
                aria-hidden="true"
              />
              <span className={styles.categoryLabel}>
                {t(`science.askCategory.${primary}`)}
              </span>
            </span>
            <span className={styles.eyebrowGroup}>
              {question.cohort ? (
                <span className={styles.eyebrow}>{question.cohort}</span>
              ) : null}
            </span>
          </div>

          <h1 className={styles.heading}>{article.title}</h1>
          {article.dek ? <p className={styles.deck}>{article.dek}</p> : null}
          {namedReview ? (
            <p className={styles.reviewedBy}>
              <span className={styles.reviewedIcon} aria-hidden="true">
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="m8.4 12.2 2.4 2.4 4.8-5.2" />
                </svg>
              </span>
              {t('science.explainers.meta.medicallyReviewedBy', {
                name: namedReview.credentials
                  ? `${namedReview.name}, ${namedReview.credentials}`
                  : namedReview.name,
              })}
              {article.lastReviewedLabel ? ` · ${article.lastReviewedLabel}` : ''}
            </p>
          ) : null}
          {authors ? (
            <p className={styles.writtenBy}>
              {t('science.explainers.meta.writtenBy', { names: authors })}
            </p>
          ) : null}

          <div className={styles.separator} role="presentation" />

          {article.stand ? (
            <p className={styles.stand}>{article.stand}</p>
          ) : null}
          {article.askNote ? (
            <p className={styles.askNote}>{article.askNote}</p>
          ) : null}

          <div className={styles.prose}>
            {blocks.map((b, i) =>
              'figureId' in b ? (
                <AskFigure
                  key={b.figureId}
                  meta={figureMeta.get(b.figureId)!}
                  data={figures[b.figureId]}
                  category={primary}
                />
              ) : (
                <RichText
                  key={`html-${i}`}
                  variant="dark"
                  html={b.html}
                  className={styles.proseBody}
                />
              ),
            )}
          </div>
        </div>

        {article.referencesHtml ? (
          /* No separator here: the "References" heading is authored at the end
             of bodyHtml and already carries the section rule above it, so a
             second line between the heading and the list was drawing twice. */
          <div className={styles.refContainer}>
            <ReferenceList
              $variant="dark"
              dangerouslySetInnerHTML={{ __html: article.referencesHtml }}
            />
          </div>
        ) : null}

        {/* THE DISCLAIMER HAS TWO SOURCES, AND THE ORDER MATTERS.
         *
         * First choice is the paragraph peeled out of the body, because that is
         * the copy an editor last touched and it may carry inline markup.
         *
         * Second is `note`, the `observational_note` COLUMN — which the backend
         * REQUIRES on every published question, so it cannot be absent. That is
         * what makes the disclaimer structurally unlosable: the peel can fail
         * (the wording changes, a new article phrases it differently) and the
         * claim still renders.
         *
         * It also changes the failure mode, which is the real reason for it. A
         * failed peel used to leave the paragraph inside the body, rendering
         * before the references — silently wrong, and live on four articles for
         * six days before anyone noticed. Now a failed peel renders the field
         * here AND leaves the body copy in place, so the failure is a visible
         * duplicate: obvious on sight, and reported the same day.
         *
         * `note` is plain text, so it renders as a React child rather than as
         * html — nothing to escape, and nothing that can inject. */}
        {noteHtml || article.note ? (
          <div className={styles.noteContainer}>
            {noteHtml ? (
              <RichText
                variant="dark"
                html={noteHtml}
                className={styles.noteBody}
              />
            ) : (
              <p className={styles.noteBody}>{article.note}</p>
            )}
          </div>
        ) : null}

        {goDeeper.length ? (
          <div className={styles.goDeeperContainer}>
            <div className={styles.separator} role="presentation" />
            <h2 className={styles.goDeeperHeading}>
              {t('science.ask.goDeeper')}
            </h2>
            <div className={styles.relList}>
              {goDeeper.map((q) => (
                <Link
                  className={styles.relCard}
                  key={q.slug}
                  href={`/science/ask/${q.slug}`}
                  aria-label={q.question}
                >
                  {q.coverImageUrl ? (
                    <span className={styles.relImg} aria-hidden="true">
                      <img
                        className={styles.relImgEl}
                        src={q.coverImageUrl}
                        alt=""
                        loading="lazy"
                        decoding="async"
                      />
                    </span>
                  ) : null}
                  <span className={styles.relBody}>
                    <span className={styles.relCat}>
                      {ASK_CATEGORY_LABEL[q.categories[0]]}
                    </span>
                    <span className={styles.relTitle}>{q.question}</span>
                  </span>
                  <span className={styles.relArrow} aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 10 10" fill="none">
                      <path
                        fill="currentColor"
                        d="M7.703 5.8H.398V4.6h7.305l-3.36-3.36.855-.84 4.8 4.8-4.8 4.8-.855-.84 3.36-3.36Z"
                      />
                    </svg>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default AskDetail;
